import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, ArrowLeft, Save, Copy, Check } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/tools/checklists")({
  head: () => ({ meta: [{ title: "Installation Checklists" }, { name: "robots", content: "noindex" }] }),
  component: ChecklistsPage,
});

type Item = { id: string; text: string; done: boolean };
type Cl = {
  id?: string; name: string; is_template: boolean; client_name: string | null;
  status: string; items: Item[]; notes: string | null;
};

const empty = (t = false): Cl => ({ name: "", is_template: t, client_name: "", status: "open", items: [], notes: "" });

const STARTER_TEMPLATES: { name: string; items: string[] }[] = [
  { name: "Home Theater Install", items: ["Verify equipment received", "Mount projector/screen", "Run speaker wire", "Terminate connections", "Calibrate audio", "Program remote", "Client walkthrough"] },
  { name: "Network Install", items: ["Mount rack/switch", "Run CAT6 drops", "Terminate keystones", "Label all cables", "Configure switch/router", "Test all ports", "Configure Wi-Fi APs", "Speed test"] },
  { name: "Security Camera Install", items: ["Mount cameras", "Run cabling to NVR", "Configure NVR", "Set motion zones", "Test remote viewing", "Configure alerts", "Client training"] },
];

function ChecklistsPage() {
  const [list, setList] = useState<Cl[]>([]);
  const [current, setCurrent] = useState<Cl | null>(null);
  const [tab, setTab] = useState<"jobs" | "templates">("jobs");

  const load = async () => {
    const { data } = await supabase.from("install_checklists").select("*").order("created_at", { ascending: false });
    setList((data as any) || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!current || !current.name) return toast.error("Name required");
    if (current.id) {
      const { error } = await supabase.from("install_checklists").update(current).eq("id", current.id);
      if (error) return toast.error(error.message);
    } else {
      const { error } = await supabase.from("install_checklists").insert(current as any);
      if (error) return toast.error(error.message);
    }
    toast.success("Saved"); setCurrent(null); load();
  };
  const del = async (id: string) => { if (confirm("Delete?")) { await supabase.from("install_checklists").delete().eq("id", id); load(); } };
  const cloneFrom = (c: Cl) => setCurrent({ ...c, id: undefined, is_template: false, name: c.name + " — Job", status: "open", items: c.items.map(i => ({ ...i, id: crypto.randomUUID(), done: false })) });
  const useStarter = (s: typeof STARTER_TEMPLATES[number]) => setCurrent({ ...empty(true), name: s.name, items: s.items.map(t => ({ id: crypto.randomUUID(), text: t, done: false })) });

  const addItem = () => setCurrent(c => c && ({ ...c, items: [...c.items, { id: crypto.randomUUID(), text: "", done: false }] }));
  const updateItem = (i: number, patch: Partial<Item>) => setCurrent(c => c && ({ ...c, items: c.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) }));
  const removeItem = (i: number) => setCurrent(c => c && ({ ...c, items: c.items.filter((_, idx) => idx !== i) }));

  if (current) {
    const done = current.items.filter(i => i.done).length;
    return (
      <div>
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => setCurrent(null)}><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
          <Button onClick={save}><Save className="h-4 w-4 mr-1" />Save</Button>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <div><label className="text-xs text-muted-foreground">Name</label><Input value={current.name} onChange={e => setCurrent({ ...current, name: e.target.value })} /></div>
          {!current.is_template && <div><label className="text-xs text-muted-foreground">Client</label><Input value={current.client_name || ""} onChange={e => setCurrent({ ...current, client_name: e.target.value })} /></div>}
          {!current.is_template && (
            <div><label className="text-xs text-muted-foreground">Status</label>
              <select className="w-full rounded border border-input bg-background p-2" value={current.status} onChange={e => setCurrent({ ...current, status: e.target.value })}>
                <option value="open">Open</option><option value="in_progress">In Progress</option><option value="complete">Complete</option>
              </select>
            </div>
          )}
        </div>
        <div className="mt-4 flex items-center gap-2">
          <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={current.is_template} onChange={e => setCurrent({ ...current, is_template: e.target.checked })} />Template</label>
          {!current.is_template && <span className="text-sm text-muted-foreground ml-auto">{done}/{current.items.length} complete</span>}
        </div>
        <div className="mt-4 space-y-2">
          {current.items.map((it, i) => (
            <div key={it.id} className="flex items-center gap-2 rounded border p-2 bg-card">
              <button onClick={() => updateItem(i, { done: !it.done })} className={`h-6 w-6 rounded border-2 flex items-center justify-center ${it.done ? "bg-primary border-primary text-primary-foreground" : "border-input"}`}>{it.done && <Check className="h-4 w-4" />}</button>
              <Input value={it.text} onChange={e => updateItem(i, { text: e.target.value })} className={it.done ? "line-through text-muted-foreground" : ""} />
              <button onClick={() => removeItem(i)} className="text-destructive"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <Button variant="outline" size="sm" className="mt-2" onClick={addItem}><Plus className="h-4 w-4 mr-1" />Add Item</Button>
        <Textarea placeholder="Notes" className="mt-4" value={current.notes || ""} onChange={e => setCurrent({ ...current, notes: e.target.value })} />
      </div>
    );
  }

  const jobs = list.filter(l => !l.is_template);
  const templates = list.filter(l => l.is_template);
  const shown = tab === "jobs" ? jobs : templates;

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-bold">Checklists</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCurrent(empty(true))}>New Template</Button>
          <Button onClick={() => setCurrent(empty(false))}><Plus className="h-4 w-4 mr-1" />New Job</Button>
        </div>
      </div>
      <div className="mt-4 flex gap-2 border-b">
        <button onClick={() => setTab("jobs")} className={`px-3 py-2 text-sm ${tab === "jobs" ? "border-b-2 border-primary font-semibold" : "text-muted-foreground"}`}>Jobs ({jobs.length})</button>
        <button onClick={() => setTab("templates")} className={`px-3 py-2 text-sm ${tab === "templates" ? "border-b-2 border-primary font-semibold" : "text-muted-foreground"}`}>Templates ({templates.length})</button>
      </div>
      {tab === "templates" && templates.length === 0 && (
        <div className="mt-4">
          <p className="text-sm text-muted-foreground mb-2">Quick-start with a common template:</p>
          <div className="flex flex-wrap gap-2">
            {STARTER_TEMPLATES.map(s => <Button key={s.name} variant="outline" size="sm" onClick={() => useStarter(s)}>{s.name}</Button>)}
          </div>
        </div>
      )}
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {shown.map(c => {
          const done = c.items.filter(i => i.done).length;
          return (
            <div key={c.id} className="rounded-lg border border-border bg-card p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold">{c.name}</h3>
                  {!c.is_template && <p className="text-sm text-muted-foreground">{c.client_name || "—"}</p>}
                  <p className="text-xs text-muted-foreground mt-1">{done}/{c.items.length} done · {c.is_template ? "Template" : c.status}</p>
                </div>
                <button onClick={() => del(c.id!)} className="text-destructive"><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="mt-3 flex gap-2">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => setCurrent(c)}>Open</Button>
                {c.is_template && <Button size="sm" onClick={() => cloneFrom(c)}><Copy className="h-4 w-4" /></Button>}
              </div>
            </div>
          );
        })}
        {shown.length === 0 && <p className="text-muted-foreground text-sm">None yet.</p>}
      </div>
    </div>
  );
}
